/**
 * Shared Chat Type Select Component
 * Used in both CreateChat and EditChat for consistent chat type selection
 */

import type { CreateChatFormData } from '@choco-admin/types';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { ChocoSelect } from '../../../../components/FormComponents';
import {
  CHAT_TYPE_OPTIONS,
  FORM_PLACEHOLDERS,
} from '../../constants/chatOptions';

interface ChatTypeSelectProps {
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Custom label text */
  label?: string;
  /** Custom placeholder text */
  placeholder?: string;
  /** Additional CSS classes */
  className?: string;
}

export const ChatTypeSelect: React.FC<ChatTypeSelectProps> = ({
  required = true,
  disabled = false,
  label = 'Chat Type',
  placeholder = FORM_PLACEHOLDERS.chatType,
  className = '',
}) => {
  const { control } = useFormContext<CreateChatFormData>();

  // Create a mutable copy of the readonly array for ChocoSelect
  const selectOptions = [...CHAT_TYPE_OPTIONS];

  return (
    <Controller
      name="chatType"
      control={control}
      rules={{
        required: required ? 'Chat type is required' : false,
      }}
      render={({ field, fieldState }) => (
        <ChocoSelect
          label={label}
          placeholder={placeholder}
          options={selectOptions}
          value={field.value?.value}
          aria-label={label}
          onSelectionChange={(selectedValue) => {
            if (selectedValue) {
              const option = selectOptions.find(
                (opt) => opt.value === selectedValue
              );
              if (option) {
                field.onChange({
                  value: option.value,
                  label: option.label,
                });
              }
            }
          }}
          errorMessage={fieldState.error?.message}
          isRequired={required}
          isDisabled={disabled}
          className={className}
        />
      )}
    />
  );
};
